import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Plus, X, Loader2, Check } from 'lucide-react';
import { useNoteStore } from '../stores/noteStore';
import { imageService } from '../services/imageService';
import { Note } from '../types';

interface ShareReceiverProps {
  sharedFiles: File[];
  sharedText?: string;
  sharedTitle?: string;
  onComplete: () => void;
  onCancel: () => void;
}

const ShareReceiver: React.FC<ShareReceiverProps> = ({
  sharedFiles,
  sharedText,
  sharedTitle,
  onComplete,
  onCancel,
}) => {
  const { notes, createNote, updateNote, setCurrentNote } = useNoteStore();
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [createNew, setCreateNew] = useState(true);
  const [newNoteTitle, setNewNoteTitle] = useState(sharedTitle || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter notes for selection (exclude password notes)
  const selectableNotes = notes
    .filter(n => n.type !== 'password')
    .filter(n => !searchQuery || n.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 10);

  const handleSave = async () => {
    setIsUploading(true);
    setError(null);

    try {
      let targetNote: Note;

      if (createNew) {
        // Create a new note
        targetNote = await createNote('text');
        if (newNoteTitle) {
          await updateNote(targetNote.id, { title: newNoteTitle });
          targetNote = { ...targetNote, title: newNoteTitle };
        }
        // Add shared text as content if provided
        if (sharedText) {
          await updateNote(targetNote.id, { content: sharedText });
        }
      } else if (selectedNote) {
        targetNote = selectedNote;
        // Append shared text to existing content if provided
        if (sharedText) {
          const newContent = selectedNote.content 
            ? `${selectedNote.content}\n\n${sharedText}`
            : sharedText;
          await updateNote(targetNote.id, { content: newContent });
        }
      } else {
        throw new Error('Please select a note or create a new one');
      }

      // Upload images
      if (sharedFiles.length > 0) {
        const existingImages = targetNote.images || [];
        const newImages = [...existingImages];

        for (let i = 0; i < sharedFiles.length; i++) {
          setUploadProgress(`Uploading ${i + 1}/${sharedFiles.length}...`);
          const image = await imageService.uploadImage(targetNote.id, sharedFiles[i]);
          newImages.push(image);
        }

        await updateNote(targetNote.id, { images: newImages });
      }

      // Navigate to the note
      setCurrentNote(targetNote);
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gradient-to-br from-yellow-100 to-yellow-50 z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-yellow-200 bg-white/80">
        <h1 className="text-lg font-semibold text-gray-800">Share to Membank</h1>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Preview of shared content */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-600 mb-3">Sharing</h2>
          
          {/* Image previews */}
          {sharedFiles.length > 0 && (
            <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
              {sharedFiles.map((file, index) => (
                <div key={index} className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Shared text preview */}
          {sharedText && (
            <div className="p-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 line-clamp-3">
              {sharedText}
            </div>
          )}
        </div>

        {/* Destination selection */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-600 mb-3">Save to</h2>

          {/* Create new note option */}
          <button
            onClick={() => { setCreateNew(true); setSelectedNote(null); }}
            className={`w-full flex items-center gap-3 p-4 rounded-xl mb-3 transition-colors ${
              createNew 
                ? 'bg-yellow-200 border-2 border-yellow-400' 
                : 'bg-white border-2 border-gray-200'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              createNew ? 'bg-yellow-400' : 'bg-gray-100'
            }`}>
              <Plus className={`w-5 h-5 ${createNew ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900">New Note</p>
              <p className="text-xs text-gray-500">Create a new note with this content</p>
            </div>
            {createNew && <Check className="w-5 h-5 text-yellow-600" />}
          </button>

          {/* New note title input */}
          {createNew && (
            <input
              type="text"
              value={newNoteTitle}
              onChange={(e) => setNewNoteTitle(e.target.value)}
              placeholder="Note title (optional)"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          )}

          {/* Existing notes */}
          <div className="space-y-2">
            <p className="text-xs text-gray-500 mb-2">Or add to existing note:</p>
            
            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes..."
              className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 mb-2"
            />

            {selectableNotes.map((note) => (
              <button
                key={note.id}
                onClick={() => { setSelectedNote(note); setCreateNew(false); }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                  selectedNote?.id === note.id && !createNew
                    ? 'bg-yellow-200 border-2 border-yellow-400' 
                    : 'bg-white border-2 border-gray-200'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  note.type === 'checklist' ? 'bg-blue-100' : 'bg-yellow-100'
                }`}>
                  <FileText className={`w-4 h-4 ${
                    note.type === 'checklist' ? 'text-blue-600' : 'text-yellow-600'
                  }`} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-medium text-gray-900 truncate">{note.title || 'Untitled'}</p>
                  <p className="text-xs text-gray-500">
                    {note.images?.length ? `${note.images.length} images • ` : ''}
                    {new Date(note.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                {selectedNote?.id === note.id && !createNew && (
                  <Check className="w-5 h-5 text-yellow-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg mb-4">
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-yellow-200 bg-white/80">
        <button
          onClick={handleSave}
          disabled={isUploading || (!createNew && !selectedNote)}
          className="w-full py-3 bg-yellow-400 text-white font-medium rounded-xl hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {uploadProgress || 'Saving...'}
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Save
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default ShareReceiver;
